import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import 'bootstrap-icons/font/bootstrap-icons.css';
import ApiService from '../../api/ApiService';
import LocalDataService from '../../api/LocalDataService';
import CanvasWorkspaceSidebar from '../../components/CanvasWorkspaceSidebar/CanvasWorkspaceSidebar';
import {
  formatTaskPoints,
  formatTaskTime,
  getTaskModelName,
  getTaskPreview,
  getTaskPrompt,
  getTaskStatusMeta
} from '../../utils/taskRecord';
import './TaskRecordsPage.css';

const PAGE_SIZE = 12;

const STATUS_FILTERS = [
  { label: '全部', value: '' },
  { label: '已完成', value: 'completed' },
  { label: '处理中', value: 'pending,processing' },
  { label: '失败', value: 'failed,webhook_error' }
];

const PREVIEW_TYPE_LABELS = {
  image: '图片',
  video: '视频',
  audio: '音频'
};

function TaskRecordsPageModal({ task, closeButtonRef, onClose }) {
  const preview = getTaskPreview(task);
  const statusMeta = getTaskStatusMeta(task.status);
  const modelName = getTaskModelName(task.app_name);
  const prompt = getTaskPrompt(task);
  const taskId = String(task.task_id || task.id || '');
  const [copyState, setCopyState] = useState('idle');
  const copyResetTimer = useRef(null);

  useEffect(() => () => {
    if (copyResetTimer.current) window.clearTimeout(copyResetTimer.current);
  }, []);

  const setTemporaryCopyState = (nextState) => {
    setCopyState(nextState);
    if (copyResetTimer.current) window.clearTimeout(copyResetTimer.current);
    copyResetTimer.current = window.setTimeout(() => {
      setCopyState('idle');
      copyResetTimer.current = null;
    }, 1600);
  };

  const handleCopyTaskId = async () => {
    if (!taskId) return;
    if (!navigator.clipboard?.writeText) {
      setTemporaryCopyState('error');
      return;
    }

    try {
      await navigator.clipboard.writeText(taskId);
      setTemporaryCopyState('copied');
    } catch (_) {
      setTemporaryCopyState('error');
    }
  };

  const copyLabel = copyState === 'copied'
    ? '任务 ID 已复制'
    : copyState === 'error'
      ? '复制任务 ID 失败'
      : '复制任务 ID';
  const copyIcon = copyState === 'copied'
    ? 'bi-check2'
    : copyState === 'error'
      ? 'bi-exclamation-circle'
      : 'bi-copy';

  return (
    <div
      className="TaskRecordsPage-modal-backdrop"
      role="presentation"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <section
        className="TaskRecordsPage-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="TaskRecordsPage-modal-title"
        aria-describedby="TaskRecordsPage-modal-prompt"
      >
        <header className="TaskRecordsPage-modal-header">
          <div>
            <h2 id="TaskRecordsPage-modal-title">任务详情</h2>
            <p>{modelName}</p>
          </div>
          <button
            ref={closeButtonRef}
            type="button"
            className="TaskRecordsPage-modal-close"
            aria-label="关闭任务详情"
            title="关闭任务详情"
            onClick={onClose}
          >
            <i className="bi bi-x-lg" aria-hidden="true" />
          </button>
        </header>

        <div className="TaskRecordsPage-modal-body">
          <div className={`TaskRecordsPage-modal-preview${preview ? '' : ' is-empty'}`}>
            {preview?.type === 'image' && <img src={preview.url} alt={`${modelName}任务结果`} />}
            {preview?.type === 'video' && <video src={preview.url} controls playsInline preload="metadata" />}
            {preview?.type === 'audio' && <audio src={preview.url} controls preload="none" />}
            {!preview && (
              <div className="TaskRecordsPage-modal-preview-empty">
                <i className="bi bi-hourglass-split" aria-hidden="true" />
                <span>{statusMeta.label}</span>
              </div>
            )}
          </div>

          <div className="TaskRecordsPage-modal-info">
            <dl className="TaskRecordsPage-modal-facts">
              <div>
                <dt>模型</dt>
                <dd>{modelName}</dd>
              </div>
              <div>
                <dt>任务 ID</dt>
                <dd className="TaskRecordsPage-modal-task-id">
                  <span className="TaskRecordsPage-modal-task-id-value">{taskId || '未知'}</span>
                  {taskId && (
                    <button
                      type="button"
                      className={'TaskRecordsPage-copy-id' + (copyState === 'idle' ? '' : ' is-' + copyState)}
                      aria-label={copyLabel}
                      title={copyLabel}
                      onClick={handleCopyTaskId}
                    >
                      <i className={'bi ' + copyIcon} aria-hidden="true" />
                      {copyState === 'copied' ? '已复制' : copyState === 'error' ? '复制失败' : '复制'}
                    </button>
                  )}
                </dd>
              </div>
              <div>
                <dt>状态</dt>
                <dd><span className={`TaskRecordsPage-status ${statusMeta.className}`}><i />{statusMeta.label}</span></dd>
              </div>
              <div>
                <dt>消耗</dt>
                <dd>{formatTaskPoints(task.money)} 积分</dd>
              </div>
              <div>
                <dt>创建时间</dt>
                <dd>{formatTaskTime(task.created_at, true)}</dd>
              </div>
              {preview && (
                <div>
                  <dt>结果类型</dt>
                  <dd>{PREVIEW_TYPE_LABELS[preview.type] || '文件'}</dd>
                </div>
              )}
            </dl>

            <div className="TaskRecordsPage-modal-prompt">
              <span>提示词</span>
              <p id="TaskRecordsPage-modal-prompt">{prompt || '该任务未记录提示词'}</p>
            </div>

            {preview?.url && (
              <a className="TaskRecordsPage-modal-result-link" href={preview.url} target="_blank" rel="noreferrer">
                <i className="bi bi-box-arrow-up-right" aria-hidden="true" />
                打开结果
              </a>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}

function TaskRecordsPage() {
  const history = useHistory();
  const user = LocalDataService.load_user_data();
  const requestVersion = useRef(0);
  const [tasks, setTasks] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [activeStatus, setActiveStatus] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [error, setError] = useState('');
  const [selectedTask, setSelectedTask] = useState(null);
  const modalCloseButtonRef = useRef(null);

  const loadTasks = useCallback(async (nextPage = 1, append = false) => {
    if (!user?.token) return;
    const version = ++requestVersion.current;
    if (append) setIsLoadingMore(true);
    else setIsLoading(true);
    setError('');

    try {
      const response = await new ApiService().get_fal_task_list(
        user.token,
        nextPage,
        PAGE_SIZE,
        '',
        activeStatus,
        { silent: true }
      );
      if (version !== requestVersion.current) return;
      if (response?.code !== 200) throw new Error('任务记录加载失败');

      const nextTasks = Array.isArray(response?.data?.list) ? response.data.list : [];
      setTasks((current) => (append ? [...current, ...nextTasks] : nextTasks));
      setTotal(Number(response?.data?.total) || 0);
      setPage(nextPage);
    } catch (_) {
      if (version === requestVersion.current) setError('任务记录加载失败，请稍后重试');
    } finally {
      if (version === requestVersion.current) {
        setIsLoading(false);
        setIsLoadingMore(false);
      }
    }
  }, [activeStatus, user?.token]);

  useEffect(() => {
    setTasks([]);
    setTotal(0);
    setPage(1);
    loadTasks(1, false);
  }, [loadTasks]);

  useEffect(() => {
    if (!selectedTask) return undefined;

    const closeOnEscape = (event) => {
      if (event.key === 'Escape') setSelectedTask(null);
    };
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    document.addEventListener('keydown', closeOnEscape);
    modalCloseButtonRef.current?.focus();

    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', closeOnEscape);
    };
  }, [selectedTask]);

  const hasMore = tasks.length < total;

  return (
    <>
      <CanvasWorkspaceSidebar
        activeKey="assets"
        onInspiration={() => history.push('/')}
        onGenerate={() => history.push('/generate')}
        onAssets={() => {}}
        onCanvas={() => history.push({ pathname: '/', search: '?tab=mine' })}
      />
      <main className="TaskRecordsPage-root">
        <section className="TaskRecordsPage-content">
          <div className="TaskRecordsPage-toolbar">
            <div className="TaskRecordsPage-filters" aria-label="任务状态筛选">
              {STATUS_FILTERS.map((filter) => (
                <button
                  className={activeStatus === filter.value ? 'is-active' : ''}
                  type="button"
                  key={filter.value}
                  aria-pressed={activeStatus === filter.value}
                  onClick={() => setActiveStatus(filter.value)}
                >
                  {filter.label}
                </button>
              ))}
            </div>
            <span className="TaskRecordsPage-sync"><i /> 与任务中心实时同步</span>
          </div>

          {isLoading && tasks.length === 0 && (
            <div className="TaskRecordsPage-state" aria-live="polite">
              <i className="TaskRecordsPage-spinner" />
              <strong>正在读取任务记录</strong>
              <span>请稍候</span>
            </div>
          )}

          {!isLoading && error && (
            <div className="TaskRecordsPage-state is-error" role="alert">
              <strong>{error}</strong>
              <button type="button" onClick={() => loadTasks(1, false)}>重新加载</button>
            </div>
          )}

          {!isLoading && !error && tasks.length === 0 && (
            <div className="TaskRecordsPage-state">
              <span className="TaskRecordsPage-empty-mark">AI</span>
              <strong>暂无任务记录</strong>
              <span>完成一次创作后，任务会自动出现在这里</span>
            </div>
          )}

          {tasks.length > 0 && (
            <div className="TaskRecordsPage-grid">
              {tasks.map((task) => {
                const preview = getTaskPreview(task);
                const statusMeta = getTaskStatusMeta(task.status);
                const isFailedTask = task.status === 'failed' || task.status === 'webhook_error';
                const prompt = getTaskPrompt(task);
                const taskId = String(task.task_id || task.id || '');

                return (
                  <article className="TaskRecordsPage-card" key={task.id || task.task_id}>
                    <div className="TaskRecordsPage-card-header">
                      <div>
                        <strong title={task.app_name}>{getTaskModelName(task.app_name)}</strong>
                        <span title={taskId}>{taskId ? `#${taskId.slice(-12)}` : '任务 ID 未知'}</span>
                      </div>
                      <span className={`TaskRecordsPage-status ${statusMeta.className}`}>
                        <i /> {statusMeta.label}
                      </span>
                    </div>

                    <div className={`TaskRecordsPage-media${preview ? '' : ' is-empty'}`}>
                      {preview?.type === 'image' && (
                        <button
                          type="button"
                          className="TaskRecordsPage-media-button"
                          aria-label={`查看${getTaskModelName(task.app_name)}任务详情`}
                          title="查看任务详情"
                          onClick={() => setSelectedTask(task)}
                        >
                          <img src={preview.url} alt={`${getTaskModelName(task.app_name)} 输出`} loading="lazy" />
                          <span className="TaskRecordsPage-media-open" aria-hidden="true"><i className="bi bi-arrows-angle-expand" /></span>
                        </button>
                      )}
                      {preview?.type === 'video' && (
                        <div className="TaskRecordsPage-media-video">
                          <video src={preview.url} controls preload="metadata" playsInline />
                          <button
                            type="button"
                            className="TaskRecordsPage-media-open"
                            aria-label={'查看' + getTaskModelName(task.app_name) + '任务详情'}
                            title="查看任务详情"
                            onClick={() => setSelectedTask(task)}
                          >
                            <i className="bi bi-arrows-angle-expand" aria-hidden="true" />
                          </button>
                        </div>
                      )}
                      {preview?.type === 'audio' && (
                        <div className="TaskRecordsPage-audio">
                          <span aria-hidden="true">♪</span>
                          <audio src={preview.url} controls preload="none" />
                        </div>
                      )}
                      {!preview && (
                        isFailedTask ? (
                          <button
                            type="button"
                            className="TaskRecordsPage-media-button TaskRecordsPage-media-placeholder-button"
                            aria-label={'查看' + getTaskModelName(task.app_name) + '任务详情'}
                            title="查看任务详情"
                            onClick={() => setSelectedTask(task)}
                          >
                            <div className="TaskRecordsPage-media-placeholder">
                              <span>!</span>
                              <small>{statusMeta.label}</small>
                            </div>
                            <span className="TaskRecordsPage-media-open" aria-hidden="true">
                              <i className="bi bi-info-circle" />
                            </span>
                          </button>
                        ) : (
                          <div className="TaskRecordsPage-media-placeholder">
                            <span>AI</span>
                            <small>{statusMeta.label}</small>
                          </div>
                        )
                      )}
                    </div>

                    <div className="TaskRecordsPage-prompt" title={prompt || '暂无提示词'}>
                      {prompt || '该任务未记录提示词'}
                    </div>

                    <footer className="TaskRecordsPage-card-footer">
                      <strong>{formatTaskPoints(task.money)} <small>积分</small></strong>
                      <time dateTime={task.created_at || ''}>{formatTaskTime(task.created_at, true)}</time>
                    </footer>
                  </article>
                );
              })}
            </div>
          )}

          {tasks.length > 0 && (
            <div className="TaskRecordsPage-pagination">
              {hasMore ? (
                <button type="button" disabled={isLoadingMore} onClick={() => loadTasks(page + 1, true)}>
                  {isLoadingMore ? '正在加载…' : '加载更多任务'}
                </button>
              ) : (
                <span>已展示全部任务</span>
              )}
            </div>
          )}
        </section>
        {selectedTask && (
          <TaskRecordsPageModal
            task={selectedTask}
            closeButtonRef={modalCloseButtonRef}
            onClose={() => setSelectedTask(null)}
          />
        )}
      </main>
    </>
  );
}

export default TaskRecordsPage;
